import { useEffect, useRef, useState} from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import useStaffStore from '../../states/staff';

import { useLocation, useNavigate } from 'react-router-dom';


import Page from '../../Components/Page/BookingPage';
import ScheduleSummary from '../../Components/Book/ScheduleSummary/ScheduleSummary';

import { getAvailability } from '../../services/Calendar';
import Calendar from '../../Components/Calendar/Calendar';
import styles from './schedule.module.css'

function formatTimeSlot(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? ''));

    if (!match) {
        return value;
    }

    const hours = Number(match[1]);
    const period = hours >= 12 ? 'PM' : 'AM';

    return `${hours % 12 || 12}:${match[2]} ${period}`;
}

function Schedule(props = {}) {
    const navigate = useNavigate();
    const today = new Date();
    const defaultMonthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const [selectedMonth, setSelectedMonth] = useState(defaultMonthValue);
    const [hasLoadedAvailability, setHasLoadedAvailability] = useState(false);
    const location = useLocation();
    const routeState = location?.state ?? {};
    const [availability, setAvailability] = useState([]);
    const staff = useStaffStore((state) => state.staff);
    const [selectedStaffID, setSelectedStaffID] = useState(staff[0]?.id ?? null);
    const staffSelectionInitialized = useRef(staff.length > 0);
    const [selectedSlot, setSelectedSlot] = useState(null);


    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [selectedDate, setSelectedDate] = useState(todayKey);


    const toggleStaffSelection = (staffMemberId) => {
        setSelectedStaffID((currentStaffID) =>
            currentStaffID === staffMemberId ? null : staffMemberId
        );
    };

    const handleDateSelect = (dateKey) => {
        setSelectedDate((current) => (current === dateKey ? null : dateKey));
        setSelectedSlot(null);
    };

    function handlePrevMonth(){
        const [year, month] = selectedMonth.split('-').map(Number);
        const prevMonth = new Date(year, month - 2, 1);
        setSelectedMonth(`${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`);
    };

    function handleNextMonth() {
        const [year, month] = selectedMonth.split('-').map(Number);
        const nextMonth = new Date(year, month, 1);
        setSelectedMonth(`${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`);
    };

    const visibleAvailability = selectedDate
        ? (availability ?? []).filter((slot) => slot?.date === selectedDate)
        : availability;

    const selectedServices = Array.isArray(routeState.selected_services) && routeState.selected_services.length > 0
        ? routeState.selected_services
        : (routeState.product ? [{
            product: routeState.product,
            selected_attributes: routeState.selected_attributes ?? routeState.attributes ?? [],
            attribute_selection: routeState.attribute_selection ?? {},
            product_id: routeState.product_id ?? routeState.product?.id,
            product_name: routeState.product_name ?? routeState.product?.name,
            product_description: routeState.product_description ?? routeState.product?.description,
            attributes: routeState.attributes ?? routeState.selected_attributes ?? [],
            total_price: routeState.total_price ?? 0,
            total_duration: routeState.total_duration ?? 0,
        }] : []);

    const primaryService = selectedServices[0] ?? {};
    const totalDuration = selectedServices.reduce(
        (sum, service) => sum + (Number(service?.total_duration) || 0),
        0
    );
    const totalPrice = selectedServices.reduce(
        (sum, service) => sum + (Number(service?.total_price) || 0),
        0
    );

    const {
        product,
        selected_attributes = Array.isArray(primaryService.selected_attributes) ? primaryService.selected_attributes : [],
        attribute_selection = {},
        product_id,
        product_name,
        product_description,
        attributes = Array.isArray(primaryService.attributes) ? primaryService.attributes : [],
    } = {
        ...routeState,
        ...props,
    };

    const handleSlotSelect = (date, slotValue) => {
        const slotKey = `${date}-${String(slotValue)}`;
        const nextSelectedSlot = selectedSlot === slotKey ? null : slotKey;
        setSelectedSlot(nextSelectedSlot);

        if (!nextSelectedSlot) {
            return;
        }

        const checkoutState = {
            selected_services: selectedServices,
            selected_attributes,
            attribute_selection,
            product_id,
            product_name,
            total_price: totalPrice,
            total_duration: totalDuration,
            date,
            slotValue,
            slotKey,
            user_id: selectedStaffID,
        };

        navigate(`/checkout/${slotKey}`, { state: checkoutState });
    };

    useEffect(() => {
        if (!staffSelectionInitialized.current && staff.length > 0) {
            setSelectedStaffID(staff[0].id);
            staffSelectionInitialized.current = true;
        }
    }, [staff]);

    useEffect(() => {
        setSelectedDate(selectedMonth === defaultMonthValue ? todayKey : null);

        // wait for rapid month navigation to settle before fetching
        let isCurrentRequest = true;
        setHasLoadedAvailability(false);
        const timeoutId = setTimeout(async () => {
            try {
                const selectedMonthDate = new Date(selectedMonth + '-01T00:00:00');
                const startDate = selectedMonth === defaultMonthValue
                    ? new Date(`${todayKey}T00:00:00`)
                    : new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), 1);

                const response = await getAvailability(
                    selectedStaffID,
                    selectedMonth,
                    totalDuration,
                    startDate.toISOString().slice(0, 10)
                );
                if (isCurrentRequest) {
                    setAvailability(response?.availability ?? response?.data ?? []);
                }
            } catch (error) {
                console.error('Error checking availability:', error);
                if (isCurrentRequest) {
                    setAvailability([]);
                }
            } finally {
                if (isCurrentRequest) {
                    setHasLoadedAvailability(true);
                }
            }
        }, 300);
        return () => {
            isCurrentRequest = false;
            clearTimeout(timeoutId);
        };
    }, [selectedMonth, selectedStaffID, totalDuration, defaultMonthValue, todayKey]);

    const formattedMonth = new Date(`${selectedMonth}-01T00:00:00`).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });

    return (
        <Page
            title="Schedule Appointment"
            subtitle="Select a timeslot to continue"
        >
            <div className={styles.scheduler}>
                <div className={styles.card}>
                    <div className={styles.calendarHeader}>
                        <button
                            type="button"
                            className={styles.navButton}
                            onClick={handlePrevMonth}
                            disabled={selectedMonth === defaultMonthValue}
                            aria-label="Previous month"
                        >
                            <LuChevronLeft />
                        </button>
                        <h2 className={styles.monthTitle}>{formattedMonth}</h2>
                        <button
                            type="button"
                            className={styles.navButton}
                            onClick={handleNextMonth}
                            aria-label="Next month"
                        >
                            <LuChevronRight />
                        </button>
                    </div>
                    <Calendar
                        availability={availability}
                        selectedMonth={selectedMonth}
                        selectedDate={selectedDate}
                        onSelectDate={handleDateSelect}
                    />
                </div>

                {hasLoadedAvailability && visibleAvailability && selectedDate && visibleAvailability.map((slot, index) => (
                    <div key={`${slot?.date ?? 'slot'}-${index}`} className={styles.card}>
                        <h3 className={styles.slotDate}>
                            {slot?.date
                                ? new Date(`${slot.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                                : 'Date'}
                        </h3>
                        {Array.isArray(slot?.available_slots) && slot.available_slots.length > 0 ? (
                            <div className={styles.slots}>
                                {slot.available_slots.map((timeSlot, slotIndex) => {
                                    const timeValue = typeof timeSlot === 'string'
                                        ? timeSlot
                                        : (timeSlot.start ?? timeSlot?.Start ?? timeSlot?.time ?? '—');

                                    const slotKey = `${slot?.date ?? 'slot'}-${timeValue}`;
                                    const isSelected = selectedSlot === slotKey;

                                    return (
                                        <button
                                            key={`${slotKey}-${slotIndex}`}
                                            type="button"
                                            aria-pressed={isSelected}
                                            onClick={() => handleSlotSelect(slot?.date ?? '', timeValue)}
                                            className={`${styles.slot}${isSelected ? ` ${styles.slotSelected}` : ''}`}
                                        >
                                            {formatTimeSlot(timeValue)}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className={styles.emptySlots}>No available times on this day.</p>
                        )}
                    </div>
                ))}
            </div>

            <ScheduleSummary
                selectedServices={selectedServices}
                duration={totalDuration}
                price={totalPrice}
                staff={staff}
                onToggleStaff={(member) => toggleStaffSelection(member.id)}
                selectedStaffID={selectedStaffID}
            />
        </Page>
    );
}

export default Schedule;